<script lang="ts">
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	//Formating
	import { format } from 'svelte-i18n';
	//Services
	import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
	// Stores
	import { user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { devices_config } from '@devices/index';
	// Components
	import MonthlyReportsStatistic from './MonthlyReportsStatistic.svelte';
	import MonthlyReportsChart from './MonthlyReportsChart.svelte';
	import MonthlyReportsListCard from './MonthlyReportsListCard.svelte';
	// Design system components
	import { Alert, CardGrid, Loader, Select } from '@Shelly/shelly-design-system';
	import PremiumUpsellAlert from 'src/components/Premium/PremiumUpsellAlert.svelte';
	// Types
	import type { RequestMonthlyReport } from 'src/types/requests/charts/get_stats_period';
	import type { Device } from 'src/types/Device';
	import { addDate, digitFormat, getMonthsOptions } from 'src/helpers/date_helper';
	import { capFirstChar } from 'src/helpers/utilities';

	import { navigateToShop } from '../Lists/list-helpers';
	import EmptyList from '../Lists/EmptyList.svelte';
	import EmptyPlaceholder from '../EmptyPlaceholder.svelte';
	let consumableDevices = Object.values($devices).filter(
		(device: Device) => devices_config[device.type]?.isPM && !device.shared,
	);

	const today = new Date();
	const startMonthlyReportDate = new Date('08/01/2023').getTime();
	const defaultMonthsOptions = getMonthsOptions();
	const currentTime = addDate(today, 'm', today.getDate() == 1 ? -2 : -1);

	const createMonths = () => {
		const currentMonth = currentTime.getMonth(),
			monthsLength = defaultMonthsOptions.length,
			months = [];
		for (let i = 0; i < monthsLength; ++i) {
			const index =
					currentMonth - i + (currentMonth < defaultMonthsOptions[i].value ? monthsLength : 0),
				month = defaultMonthsOptions[index],
				date = addDate(currentTime, 'm', -i);
			if (i == 0 || date.getTime() > startMonthlyReportDate) {
				months.push({ ...month, year: date.getFullYear() });
			}
		}
		return months;
	};

	let months = createMonths();
	const currency = get(user_settings)?.currency || '';

	const getAvailableMonths = async () => {
		const res = await CacheStatisticRequest.getData(
			'/v2/users/monthly-reports',
			{},
			undefined,
			true,
		);
		if (res && Array.isArray(res) && res.length > 0) {
			months = months.map((month) => {
				let date = `${month.year}-${digitFormat(month.value + 1)}`;
				let disabled = false;
				if (!res.includes(date)) {
					disabled = true;
				}
				return { ...month, disabled };
			});
		} else {
			months = months.map((month) => ({ ...month, disabled: true }));
		}
	};

	onMount(() => {
		getAvailableMonths();
	});

	const getMonthlyReportData = async (): Promise<RequestMonthlyReport> => {
		if (!$user_data?.premium) {
			return {} as RequestMonthlyReport;
		}

		const res = await CacheStatisticRequest.getData(
			'/v2/users/monthly-reports',
			{
				date: `${currentTime.getFullYear()}-${digitFormat(currentTime.getMonth() + 1)}`,
			},
			undefined,
			true,
		);

		if (res?.report) return res;
		else if (res?.error) {
			throw new Error(
				$format('failed_to_get_statistics_data', {
					default: 'Failed to get statistics data',
				}),
			);
		} else {
			throw new Error(
				$format('monthly_report_doesnot_statistics_data', {
					default: "The monthly report doesn't have statistics data",
				}),
			);
		}
	};

	const monthSelect = (e: CustomEvent) => {
		currentTime.setFullYear(e.detail.year, e.detail.value, 1);
		statsData = getMonthlyReportData();
	};

	$: statsData = getMonthlyReportData();
	$: lastValidMonth = months.find((month) => typeof month.disabled === 'boolean' && !month.disabled);
	$: {
		if (lastValidMonth) {
			monthSelect({
				detail: { year: lastValidMonth.year, value: lastValidMonth.value },
			} as CustomEvent);
		}
	}
</script>

{#if !$user_data.premium}
	<PremiumUpsellAlert
		class="mb-m"
		heading={$format('heading_alert_get_premium_monthly_reports', {
			default: 'To receive the monthly reports, you need to be a premium user.',
		})}
	/>
	<!-- {:else if Object.values($devices).length === 0}
	<EmptyDeviceList />
	{/if} -->
{:else if consumableDevices.length === 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('no_power_measuring_device_title', {
				default: 'No power measuring devices.',
			})}
			description={$format('get_necessary_device_to_track_usage', {
				default: 'Get the necessary devices to start tracking energy usage.',
			})}
			btnText={capFirstChar(
				$format('button_go_to_store', {
					default: 'Go to store',
				}).toLocaleLowerCase(),
			)}
			onClick={() => {
				navigateToShop();
			}}
		/>
	</div>
{:else}
	<Select
		size="m"
		class="mt-s"
		width="full"
		icon="fa-light fa-calendar"
		options={months}
		value={lastValidMonth || months[0]}
		id="monthly-report-month-select"
		aria-label={$format('label_select_month_for_report', {
			default: 'Select month for monthly report',
		})}
		on:change={monthSelect}
	/>
	{#await statsData}
		<div class="d-flex justify-content-center align-items-center">
			<Loader size="m" />
		</div>
	{:then result}
		<MonthlyReportsStatistic
			{result}
			year={currentTime.getFullYear()}
			month={currentTime.getMonth()}
			{currency}
		/>
		<MonthlyReportsChart type="hour" {result} {currentTime} {currency} />
		<div class="mt-md-m mt-sm-s"></div>
		<MonthlyReportsChart type="day" {result} {currentTime} {currency} />
		<div class="mt-md-m mt-sm-s"></div>
		<CardGrid lg={480} sm={350}>
			<MonthlyReportsChart type="week" {result} {currentTime} {currency} />
			<MonthlyReportsListCard type="device" {result} {currency} />
			<MonthlyReportsListCard type="room" {result} {currency} />
			<!-- <MonthlyReportsListCard type="purpose" {result} {currency} /> -->
			<MonthlyReportsListCard type="peakPower" {result} />
		</CardGrid>
	{:catch error}
		<Alert class="mt-s" heading={error.message} type="warning" />
	{/await}
{/if}
